import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Slider from "react-slick";
import imageNotFound from "../../common/assets/images/imageNotFound.png";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Custom Arrow Components
const NextArrow = ({ onClick }) => (
	<button
		className="product-gallery-arrow product-gallery-arrow-next"
		onClick={onClick}
		aria-label="Next image"
		type="button"
	>
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			aria-hidden="true"
		>
			<path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	</button>
);

const PrevArrow = ({ onClick }) => (
	<button
		className="product-gallery-arrow product-gallery-arrow-prev"
		onClick={onClick}
		aria-label="Previous image"
		type="button"
	>
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			aria-hidden="true"
		>
			<path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	</button>
);

const Banner = ({ carDetail }) => {
	const location = useLocation();
	const [nav1, setNav1] = useState(null);
	const [nav2, setNav2] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const sliderRef1 = useRef(null);
	const sliderRef2 = useRef(null);

	const queryParams = new URLSearchParams(location.search);
	const photoUrls =
		location?.state?.photoUrls ||
		JSON.parse(queryParams.get("photoUrls")) ||
		JSON.parse(localStorage.getItem("photoUrls")) ||
		[];

	useEffect(() => {
		setNav1(sliderRef1.current);
		setNav2(sliderRef2.current);
	}, []);

	const mainSettings = {
		arrows: true,
		nextArrow: <NextArrow />,
		prevArrow: <PrevArrow />,
		fade: true,
		speed: 500,
		beforeChange: (_current, next) => setCurrentSlide(next),
	};

	const thumbSettings = {
		slidesToShow: Math.min(5, photoUrls?.length || 1),
		swipeToSlide: true,
		focusOnSelect: true,
		arrows: false,
		responsive: [
			{
				breakpoint: 768,
				settings: {
					slidesToShow: Math.min(4, photoUrls?.length || 1),
				},
			},
			{
				breakpoint: 480,
				settings: {
					slidesToShow: Math.min(3, photoUrls?.length || 1),
				},
			},
		],
	};

	const { year, make, model, price } = carDetail || {};

	return (
		<div className="product-hero">
			{/* Background Gradient */}
			<div className="product-hero-bg" />

			{/* Hero Content */}
			<div className="product-hero-content">
				{/* Vehicle Title */}
				<div className="product-hero-header">
					<div className="product-hero-title-section">
						{year && <span className="product-hero-year">{year}</span>}
						<h1 className="product-hero-title">
							{make} {model}
						</h1>
					</div>
					{price && <div className="product-hero-price">{price}</div>}
				</div>

				{/* Gallery Container */}
				<div className="product-gallery-container">
					{photoUrls?.length > 0 ? (
						<>
							{/* Main Image Slider */}
							<div className="product-gallery-main">
								<Slider asNavFor={nav2} ref={sliderRef1} {...mainSettings}>
									{photoUrls.map((url, index) => (
										<div key={url} className="product-gallery-slide">
											<button
												type="button"
												className="product-gallery-image-wrapper p-0 border-0 bg-transparent w-full"
												onClick={() => setLightboxOpen(true)}
											>
												<img
													src={url}
													alt={`${make} ${model} ${index + 1}`}
													className="product-gallery-image"
												/>
												{/* Zoom Icon */}
												<div className="product-gallery-zoom">
													<svg
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2"
														aria-hidden="true"
													>
														<path
															d="M21 21l-4.35-4.35M11 8v6M8 11h6"
															strokeLinecap="round"
														/>
														<circle cx="11" cy="11" r="8" />
													</svg>
												</div>
											</button>
										</div>
									))}
								</Slider>
								{/* Image Counter */}
								<div className="product-gallery-counter">
									<svg
										className="w-4 h-4"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										aria-hidden="true"
									>
										<rect x="3" y="3" width="18" height="18" rx="2" />
										<circle cx="8.5" cy="8.5" r="1.5" />
										<path d="M21 15l-5-5L5 21" />
									</svg>
									<span>
										{currentSlide + 1} / {photoUrls.length}
									</span>
								</div>
							</div>

							{/* Thumbnail Slider */}
							{photoUrls.length > 1 && (
								<div className="product-gallery-thumbs">
									<Slider asNavFor={nav1} ref={sliderRef2} {...thumbSettings}>
										{photoUrls.map((url, index) => (
											<div key={url} className="product-thumb-slide">
												<button
													type="button"
													className={`product-thumb ${currentSlide === index ? "product-thumb-active" : ""} border-0 p-0 bg-transparent w-full`}
													onClick={() => sliderRef1.current?.slickGoTo(index)}
												>
													<img src={url} alt={`Slide ${index + 1}`} />
												</button>
											</div>
										))}
									</Slider>
								</div>
							)}
						</>
					) : (
						<div className="product-gallery-empty">
							<img src={imageNotFound} alt="Not found" />
							<p>No images available</p>
						</div>
					)}
				</div>
			</div>

			{/* Lightbox */}
			{lightboxOpen && photoUrls?.length > 0 && (
				<div
					className="product-lightbox"
					onClick={() => setLightboxOpen(false)}
					role="button"
					tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") setLightboxOpen(false);
					}}
				>
					<button
						className="product-gallery-view-all"
						onClick={() => setLightboxOpen(true)}
						type="button"
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							aria-hidden="true"
						>
							<path
								d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<circle
								cx="12"
								cy="12"
								r="3"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						View All
					</button>
					<button
						className="product-lightbox-close"
						onClick={() => setLightboxOpen(false)}
						type="button"
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							aria-hidden="true"
						>
							<path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
						</svg>
					</button>
					<button
						type="button"
						className="product-lightbox-image-wrapper p-0 border-0 bg-transparent"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						<img src={photoUrls[currentSlide]} alt={`${make} ${model}`} />
					</button>
				</div>
			)}
		</div>
	);
};

export default Banner;
